import { useState, useMemo, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { useHelpdeskNavigation } from "../hooks/useHelpdeskNavigation";
import { useAllOrganizationTickets } from "../hooks/useTickets";
import { useCustomerTickets } from "../hooks/useCustomerPortal";
import { categoriesQueryKey } from "../hooks/useCategories";
import { apiService } from "../services/api";
import { useStatuses, usePriorities } from "../hooks/useLookups";
import { useDateFormat } from "../hooks/useDateFormat";
import { useSignalR } from "../contexts/SignalRContext";
import { usePersistedFilters } from "../hooks/usePersistedFilters";
import { useScrollEdges } from "../hooks/useScrollEdges";
import { useDragScroll } from "../hooks/useDragScroll";
import { TH_CLASS, TD_CLASS } from "../lib/tableStyles";
import { queryKeys } from "../lib/queryKeys";
import { showToast } from "../lib/toast";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { TicketFilterRequest } from "../types/tickets";
import { Route } from "../routes/helpdesk/tickets/index";

// SignalR event data types
interface TicketCreatedData {
  TicketData?: {
    TicketNumber?: number;
    Id?: string;
  };
}

interface TicketUpdatedData {
  ticketId?: string;
  TicketId?: string;
  changes?: string[];
  updatedBy?: string;
}

interface TicketActivityData {
  ticketId?: string;
  TicketId?: string;
  activityType?: string;
  message?: string;
}

interface CommentAddedData {
  ticketId?: string;
  TicketId?: string;
  commentId?: string;
  CommentId?: string;
  author?: string;
}
import {
  TicketStatus,
  Priority,
  TicketStatusLabels,
  PriorityLabels,
  TicketStatusColors,
  PriorityColors,
} from "../types/tickets";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { DatePicker } from "../components/ui/date-picker";
import { TicketListSkeleton } from "../components/LoadingSkeleton";
import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { CustomerMultiSelectFilter } from "../components/CustomerMultiSelectFilter";
import { AssigneeMultiSelectFilter } from "../components/AssigneeMultiSelectFilter";
import { TreeMultiSelectFilter } from "../components/tree-select/TreeMultiSelectFilter";
import { categoryToOption } from "../components/tree-select/treeOptions";
import { CollapsibleFilterBar } from "../components/CollapsibleFilterBar";
import { AssigneeSelector } from "../components/tickets/AssigneeSelector";
import { useCustomers } from "../hooks/useCustomers";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, X, AlertTriangle, GitMerge, Eye } from "lucide-react";

interface TicketListProps {
  isCustomerPortal?: boolean;
}

export function TicketList({ isCustomerPortal = false }: TicketListProps) {
  const { navigateTo, getPath } = useHelpdeskNavigation();
  const { formatDateTime, formatDateOnly, parseDateToUTC } = useDateFormat();
  const { user, isCustomerPortal: authIsCustomerPortal, availableOrganizations } = useAuth();
  const queryClient = useQueryClient();
  const { signalR, isConnected } = useSignalR();

  // Use auth context to determine portal type if not explicitly passed
  const actualIsCustomerPortal = isCustomerPortal || authIsCustomerPortal;

  // Each step is the sum of the column widths visible at that breakpoint plus a floor for the
  // flexible Subject column, so fixed layout never scales columns below their declared widths.
  // Fixed widths total 452 (base) / 642 (md) / 1140 (lg) for staff and 292 / 650 for the portal,
  // leaving Subject a floor of 228px below lg and 240px at lg.
  //
  // These widths were measured, not guessed - most columns were already correctly sized:
  //   Status 110  - "In Progress" badge measures 79px; 100 would clip it
  //   Due Date 150 - renders date + "Overdue" badge together, 139px combined
  //   Ticket # 90  - "#123456" measures 65px + 24px padding; the previous 88 was 2px short,
  //                  and ticket numbers only ever grow (NextTicketNumber never resets)
  //   Created/Updated 104 - "Sep 11, 2026" measures 73px, leaving ~7px of headroom
  // Only Priority and the date columns had real slack, so Subject gained 18px. The readability
  // win comes from the two-line clamp on Subject below, not from these widths.
  // The staff ladder starts at 680px because Assigned To renders at every breakpoint — see the
  // note on that column. The portal ladder is shorter because it drops Organization, Customer
  // and Assigned To.
  const tableMinWidthClass = actualIsCustomerPortal
    ? "min-w-[520px] lg:min-w-[890px]"
    : "min-w-[680px] md:min-w-[870px] lg:min-w-[1380px]";

  // Always call the hook unconditionally to avoid rules of hooks violation
  const routeSearchParams = Route.useSearch();
  // Get search params from URL (only for non-customer portal)
  const searchParams = actualIsCustomerPortal ? null : routeSearchParams;

  // Define default filter values for localStorage persistence (helpdesk only)
  const defaultFilterValues = {
    sortBy: 'createdat',
    sortDescending: true,
    page: 1,
    pageSize: 20,
  };

  // Initialize filter persistence for helpdesk mode (global across all orgs)
  const storageKey = `helpdesk.tickets.filters`;
  const { updateFilters, clearFilters: clearPersistedFilters, isInitialized } = usePersistedFilters(
    storageKey,
    defaultFilterValues,
    actualIsCustomerPortal ? defaultFilterValues : (searchParams || defaultFilterValues)
  );

  // Filter state - use URL params for helpdesk, local state for customer portal
  const [searchInput, setSearchInput] = useState(
    actualIsCustomerPortal ? "" : (searchParams?.search || "")
  );

  // For customer portal, use local state (old behavior)
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerStatusFilter, setCustomerStatusFilter] = useState<TicketStatus | "">("");
  const [customerPriorityFilter, setCustomerPriorityFilter] = useState<Priority | "">("");
  const [customerAssigneeFilter, setCustomerAssigneeFilter] = useState<string>("");
  const [customerCategoryFilter, setCustomerCategoryFilter] = useState<string>("");
  const [customerFromDate, setCustomerFromDate] = useState<string>("");
  const [customerToDate, setCustomerToDate] = useState<string>("");
  const [customerSortBy, setCustomerSortBy] = useState("createdat");
  const [customerSortDescending, setCustomerSortDescending] = useState(true);
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(20);

  // Get current filter values
  const searchQuery = actualIsCustomerPortal ? customerSearchQuery : (searchParams?.search || "");
  // For helpdesk, use arrays from URL; for customer portal, convert single values to arrays
  const statusFilters: TicketStatus[] = useMemo(() => actualIsCustomerPortal
    ? (customerStatusFilter !== "" ? [customerStatusFilter] : [])
    : (searchParams?.statuses ?? []) as TicketStatus[], [actualIsCustomerPortal, customerStatusFilter, searchParams?.statuses]);
  const priorityFilters: Priority[] = useMemo(() => actualIsCustomerPortal
    ? (customerPriorityFilter !== "" ? [customerPriorityFilter] : [])
    : (searchParams?.priorities ?? []) as Priority[], [actualIsCustomerPortal, customerPriorityFilter, searchParams?.priorities]);
  const assigneeFilters: string[] = useMemo(() => actualIsCustomerPortal
    ? (customerAssigneeFilter ? [customerAssigneeFilter] : [])
    : (searchParams?.assignees ?? []), [actualIsCustomerPortal, customerAssigneeFilter, searchParams?.assignees]);
  const categoryFilters: string[] = useMemo(() => actualIsCustomerPortal
    ? (customerCategoryFilter ? [customerCategoryFilter] : [])
    : (searchParams?.categories ?? []), [actualIsCustomerPortal, customerCategoryFilter, searchParams?.categories]);
  const customerFilters: string[] = useMemo(() => actualIsCustomerPortal
    ? [] // Customers don't filter by customer in their own portal
    : (searchParams?.customers ?? []), [actualIsCustomerPortal, searchParams?.customers]);
  const organizationFilters: string[] = useMemo(() => actualIsCustomerPortal
    ? [] // Customers don't filter by organization
    : (searchParams?.organizationIds ?? []), [actualIsCustomerPortal, searchParams?.organizationIds]);
  const fromDate = actualIsCustomerPortal ? customerFromDate : (searchParams?.fromDate || "");
  const toDate = actualIsCustomerPortal ? customerToDate : (searchParams?.toDate || "");
  const sortBy = actualIsCustomerPortal ? customerSortBy : (searchParams?.sortBy || "createdat");
  const sortDescending = actualIsCustomerPortal ? customerSortDescending : (searchParams?.sortDescending ?? true);
  const currentPage = actualIsCustomerPortal ? customerCurrentPage : (searchParams?.page || 1);
  const pageSize = actualIsCustomerPortal ? customerPageSize : (searchParams?.pageSize || 20);
  const showOverdue = actualIsCustomerPortal ? false : (searchParams?.showOverdue ?? false);
  const showFollowing = actualIsCustomerPortal ? false : (searchParams?.following ?? false);

  // Build filter object for query
  const filter = useMemo(
    (): TicketFilterRequest => {
      // Separate "unassigned" from actual assignee IDs
      const hasUnassigned = assigneeFilters.includes("unassigned");
      const actualAssigneeIds = assigneeFilters.filter(id => id !== "unassigned");

      return {
        page: currentPage,
        pageSize,
        sortBy,
        sortDescending,
        ...(searchQuery && { searchQuery }),
        ...(statusFilters.length > 0 && { statuses: statusFilters }),
        ...(priorityFilters.length > 0 && { priorities: priorityFilters }),
        ...(actualAssigneeIds.length > 0 && { assignedToIds: actualAssigneeIds }),
        ...(hasUnassigned && { showUnassigned: true }),
        ...(showOverdue && { showOverdue: true }),
        ...(showFollowing && { followedByMe: true }),
        ...(categoryFilters.length > 0 && { categoryIds: categoryFilters }),
        ...(customerFilters.length > 0 && { customerIds: customerFilters }),
        ...(organizationFilters.length > 0 && { organizationIds: organizationFilters }),
        ...(fromDate && { fromDate: parseDateToUTC(fromDate, true) || undefined }), // Start of day
        ...(toDate && { toDate: parseDateToUTC(toDate, false) || undefined }),       // End of day
      };
    },
    [
      currentPage,
      pageSize,
      sortBy,
      sortDescending,
      searchQuery,
      statusFilters,
      priorityFilters,
      assigneeFilters,
      categoryFilters,
      customerFilters,
      organizationFilters,
      fromDate,
      toDate,
      showOverdue,
      showFollowing,
      parseDateToUTC,
    ]
  );

  // Calculate active filter count for the collapsible filter bar
  // Counts each filter category as 1 if it has any non-default value
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (statusFilters.length > 0) count++;
    if (priorityFilters.length > 0) count++;
    if (assigneeFilters.length > 0) count++;
    if (categoryFilters.length > 0) count++;
    if (customerFilters.length > 0) count++;
    // Only count organization filter if user has multiple orgs AND not all are selected
    if (!actualIsCustomerPortal && availableOrganizations.length > 1 &&
        organizationFilters.length > 0 && organizationFilters.length < availableOrganizations.length) {
      count++;
    }
    if (fromDate) count++;
    if (toDate) count++;
    if (showOverdue) count++;
    if (showFollowing) count++;
    return count;
  }, [
    searchQuery,
    statusFilters,
    priorityFilters,
    assigneeFilters,
    categoryFilters,
    customerFilters,
    organizationFilters,
    fromDate,
    toDate,
    showOverdue,
    showFollowing,
    actualIsCustomerPortal,
    availableOrganizations.length,
  ]);

  // Define open statuses (everything except Resolved=4 and Closed=5)
  const openStatuses = [TicketStatus.Open, TicketStatus.InProgress, TicketStatus.Pending, TicketStatus.OnHold];

  // Check if "My Open Tickets" filter is currently active
  const isMyOpenTicketsActive = useMemo(() => {
    const currentUserId = user?.id;
    if (!currentUserId) return false;

    const hasOnlyCurrentUserAssigned =
      assigneeFilters.length === 1 && assigneeFilters[0] === currentUserId;

    const hasOnlyOpenStatuses =
      statusFilters.length === openStatuses.length &&
      openStatuses.every(s => statusFilters.includes(s));

    return hasOnlyCurrentUserAssigned && hasOnlyOpenStatuses;
  }, [assigneeFilters, statusFilters, user?.id, openStatuses]);

  // Use TanStack Query hooks - call both unconditionally to avoid hooks rules violations
  const customerTicketsQuery = useCustomerTickets(filter, actualIsCustomerPortal);
  const regularTicketsQuery = useAllOrganizationTickets(filter, !actualIsCustomerPortal);

  // Choose which query to use based on portal type
  const { data, isLoading, isFetching, error, isError } = actualIsCustomerPortal
    ? customerTicketsQuery
    : regularTicketsQuery;

  // Data queries for filter options
  const { data: statuses = [] } = useStatuses();
  const { data: priorities = [] } = usePriorities();

  // Determine which organization IDs to fetch categories and users for
  // Use selected org filters, or all available organizations if none selected
  const selectedOrgIds = useMemo(() => {
    if (actualIsCustomerPortal) return [];
    return organizationFilters.length > 0
      ? organizationFilters
      : availableOrganizations.map(org => org.id);
  }, [actualIsCustomerPortal, organizationFilters, availableOrganizations]);

  // Fetch categories for all selected organizations in parallel
  const categoryQueries = useQueries({
    queries: selectedOrgIds.map(orgId => ({
      // Filters include archived categories: tickets and articles still carry them, and a saved filter on one
      // must not be pruned as invalid
      queryKey: categoriesQueryKey(orgId, true),
      queryFn: async () => {
        const response = await apiService.getCategories(orgId, { includeArchived: true });
        if (!response.success) {
          throw new Error(getApiErrorMessage(response, 'Failed to fetch categories'));
        }
        return Array.isArray(response.data) ? response.data : [];
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Fetch assignable users for all selected organizations in parallel
  const userQueries = useQueries({
    queries: selectedOrgIds.map(orgId => ({
      queryKey: ['users', 'assignable', orgId],
      queryFn: async () => {
        const response = await apiService.getUsersForAssignment(orgId);
        if (!response.success || !response.data) {
          throw new Error(getApiErrorMessage(response, 'Failed to fetch assignable users'));
        }
        return response.data;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Combine and dedupe categories from all organizations
  const safeCategories = useMemo(() => {
    const allCategories = categoryQueries
      .filter(q => q.isSuccess && q.data)
      .flatMap(q => q.data || []);
    // Dedupe by ID - keep first occurrence (categories may have same name across orgs)
    return [...new Map(allCategories.map(c => [c.id, c])).values()];
  }, [categoryQueries]);

  // Combine and dedupe users from all organizations
  const safeUsers = useMemo(() => {
    const allUsers = userQueries
      .filter(q => q.isSuccess && q.data)
      .flatMap(q => q.data || []);
    // Dedupe by ID - same user may be in multiple orgs
    return [...new Map(allUsers.map(u => [u.id, u])).values()];
  }, [userQueries]);

  // Resolve labels for currently-selected customer IDs, scoped to the in-scope orgs.
  // This drives the picker's row data and the cleanup logic below. We pass a scoped
  // filter object unconditionally (never `undefined`, which would fall back to the
  // unfiltered cross-org list in the shared hook) and gate via `enabled` so the
  // request only fires when there are actually selected customers to resolve.
  const selectedCustomersQuery = useCustomers(
    {
      ids: customerFilters,
      organizationIds: selectedOrgIds,
      pageSize: 100,
    },
    { enabled: !actualIsCustomerPortal && customerFilters.length > 0 }
  );
  const safeSelectedCustomers = useMemo(
    () => selectedCustomersQuery.data?.customers || [],
    [selectedCustomersQuery.data]
  );
  // Gate cleanup on data that matches the *current* query args. `useCustomers` is
  // configured with `placeholderData: keepPreviousData`, so `isSuccess` stays true
  // while a new fetch is in flight. `isPlaceholderData` flips to false only once the
  // new args have resolved — exactly when `safeSelectedCustomers` reflects the new scope.
  const customerLabelsLoaded =
    customerFilters.length === 0 ||
    (selectedCustomersQuery.isSuccess && !selectedCustomersQuery.isPlaceholderData);

  // Check if category and user queries have settled (to avoid premature filter clearing).
  // We must wait for *every* per-org query to finish — using `.some()` would let the cleanup
  // branch run as soon as the first org returns, with `safeUsers`/`safeCategories` still
  // missing the data from slower orgs, and incorrectly prune valid IDs from those orgs.
  const categoriesLoaded =
    categoryQueries.length === 0 ||
    categoryQueries.every(q => q.isSuccess || q.isError);
  const usersLoaded =
    userQueries.length === 0 ||
    userQueries.every(q => q.isSuccess || q.isError);

  // Create stable keys for available options to detect changes
  const availableCategoryIdsKey = useMemo(() =>
    safeCategories.map(c => c.id).sort().join(','),
    [safeCategories]
  );
  const availableUserIdsKey = useMemo(() =>
    safeUsers.map(u => u.id).sort().join(','),
    [safeUsers]
  );
  const availableCustomerIdsKey = useMemo(() =>
    safeSelectedCustomers.map(c => c.id).sort().join(','),
    [safeSelectedCustomers]
  );

  // Auto-deselect invalid category and assignee filters when available options change
  // Also clear category filters when multiple orgs are selected (category dropdown is hidden)
  useEffect(() => {
    if (actualIsCustomerPortal) return;

    // Get current filter values from URL params
    const currentCategoryFilters = searchParams?.categories ?? [];
    const currentAssigneeFilters = searchParams?.assignees ?? [];
    const currentCustomerFilters = searchParams?.customers ?? [];

    const messages: string[] = [];
    const updates: Record<string, string[] | undefined> = {};

    // Clear category filters when multiple orgs are selected (dropdown hidden)
    if (selectedOrgIds.length !== 1 && currentCategoryFilters.length > 0) {
      messages.push(`${currentCategoryFilters.length} category filter${currentCategoryFilters.length > 1 ? 's' : ''}`);
      updates.categories = undefined;
    }
    // Check for invalid categories when single org is selected
    else if (selectedOrgIds.length === 1 && categoriesLoaded && safeCategories.length > 0) {
      const validCategoryIds = new Set(safeCategories.map(c => c.id));
      const invalidCategories = currentCategoryFilters.filter(id => !validCategoryIds.has(id));
      if (invalidCategories.length > 0) {
        const validCategories = currentCategoryFilters.filter(id => validCategoryIds.has(id));
        messages.push(`${invalidCategories.length} category filter${invalidCategories.length > 1 ? 's' : ''}`);
        updates.categories = validCategories.length > 0 ? validCategories : undefined;
      }
    }

    // Check for invalid assignees
    if (usersLoaded && safeUsers.length > 0) {
      const validUserIds = new Set(safeUsers.map(u => u.id));
      validUserIds.add("unassigned"); // "unassigned" is always valid
      const invalidAssignees = currentAssigneeFilters.filter(id => !validUserIds.has(id));
      if (invalidAssignees.length > 0) {
        const validAssignees = currentAssigneeFilters.filter(id => validUserIds.has(id));
        messages.push(`${invalidAssignees.length} assignee filter${invalidAssignees.length > 1 ? 's' : ''}`);
        updates.assignees = validAssignees.length > 0 ? validAssignees : undefined;
      }
    }

    // Check for invalid customers (out of scope for the currently selected orgs)
    if (customerLabelsLoaded && currentCustomerFilters.length > 0) {
      const validCustomerIds = new Set(safeSelectedCustomers.map(c => c.id));
      const invalidCustomers = currentCustomerFilters.filter(id => !validCustomerIds.has(id));
      if (invalidCustomers.length > 0) {
        const validCustomers = currentCustomerFilters.filter(id => validCustomerIds.has(id));
        messages.push(`${invalidCustomers.length} customer filter${invalidCustomers.length > 1 ? 's' : ''}`);
        updates.customers = validCustomers.length > 0 ? validCustomers : undefined;
      }
    }

    if (Object.keys(updates).length > 0) {
      updateFilters(updates);
      // Category-specific multi-org wording only applies when categories were the cause.
      const categoryHiddenByMultiOrg =
        'categories' in updates && selectedOrgIds.length !== 1;
      const reason = categoryHiddenByMultiOrg
        ? "category filter requires single organization selection"
        : "not available in selected organization";
      showToast.info("Filters Updated", `${messages.join(' and ')} removed - ${reason}`);
    }
  }, [availableCategoryIdsKey, availableUserIdsKey, availableCustomerIdsKey, categoriesLoaded, usersLoaded, customerLabelsLoaded, selectedCustomersQuery.isPlaceholderData, actualIsCustomerPortal, updateFilters, searchParams?.categories, searchParams?.assignees, searchParams?.customers, selectedOrgIds.length, safeCategories, safeUsers, safeSelectedCustomers]);

  const safeStatuses = Array.isArray(statuses) ? statuses : [];
  const safePriorities = Array.isArray(priorities) ? priorities : [];

  const tickets = data?.tickets || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = data?.totalPages || 1;

  // Re-measure whenever the rendered width can change: new rows, a resize that crosses a
  // breakpoint and sheds a column, or the table replacing a loading skeleton (which is when
  // the scroll container first exists to measure).
  const {
    ref: tableScrollRef,
    edges: scrollEdges,
    onScroll: updateScrollEdges,
  } = useScrollEdges<HTMLDivElement>([
    tickets.length,
    actualIsCustomerPortal,
    isInitialized,
    isLoading,
  ]);

  const tableDragProps = useDragScroll(tableScrollRef);

  // SignalR real-time updates
  useEffect(() => {
    if (!isConnected || actualIsCustomerPortal) return; // Skip for customer portal

    const handleTicketCreated = (data: TicketCreatedData) => {
      console.log('[SignalR] TicketList: New ticket created:', data);

      // Invalidate all ticket lists (both single-org and all-orgs views) to show the new ticket
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tickets.allOrgsLists(),
      });

      // Toast notifications handled by global InAppNotification handler to avoid duplicates
    };

    const handleTicketUpdate = (data: TicketUpdatedData) => {
      console.log('[SignalR] TicketList: Ticket updated (from NotificationHub):', data);
      const ticketId = data.ticketId || data.TicketId;

      // Add delay to ensure backend has propagated changes
      setTimeout(() => {
        // Invalidate all ticket lists (both single-org and all-orgs views)
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.allOrgsLists(),
        });

        // Invalidate detail and history queries to prevent stale cache when navigating to ticket
        if (ticketId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(ticketId),
          });
        }
      }, 250);

      // Subtle notification - don't overwhelm with too many notifications
    };

    const handleTicketUpdated = (data: TicketUpdatedData) => {
      console.log('[SignalR] TicketList: Ticket updated (from TicketHub):', data);
      const ticketId = data.ticketId || data.TicketId;

      // Add delay to ensure backend has propagated changes
      setTimeout(() => {
        // Invalidate all ticket lists (both single-org and all-orgs views)
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.allOrgsLists(),
        });

        // Invalidate detail and history queries to prevent stale cache when navigating to ticket
        if (ticketId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(ticketId),
          });
        }
      }, 250);
    };

    const handleTicketActivity = (data: TicketActivityData) => {
      console.log('[SignalR] TicketList: Ticket activity:', data);
      const ticketId = data.ticketId || data.TicketId;

      // Add delay to ensure backend has propagated changes
      setTimeout(() => {
        // Invalidate all ticket lists (both single-org and all-orgs views)
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.allOrgsLists(),
        });

        // Invalidate detail and history queries to prevent stale cache when navigating to ticket
        if (ticketId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(ticketId),
          });
        }
      }, 250);
    };

    const handleCommentAdded = (data: CommentAddedData) => {
      console.log('[SignalR] TicketList: Comment added to ticket:', data);
      const ticketId = data.ticketId || data.TicketId;

      // Add delay to ensure backend has propagated changes
      setTimeout(() => {
        // Invalidate all ticket lists (both single-org and all-orgs views)
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.tickets.allOrgsLists(),
        });

        // Invalidate detail and history queries to prevent stale cache when navigating to ticket
        if (ticketId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.detail(ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.tickets.history(ticketId),
          });
        }
      }, 250);
    };

    // A merge changes two rows at once: the source becomes closed and badged, the
    // target's timestamp moves. Refresh the list and both tickets' cached details.
    const handleTicketMergeUpdate = (data: { sourceTicketId: string; targetTicketId: string }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.stats() });
      [data.sourceTicketId, data.targetTicketId].forEach((ticketId) => {
        if (!ticketId) return;
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
      });
    };

    // Set up event handlers - use cleanup functions returned by on()
    const cleanupTicketCreated = signalR.on('TicketCreated', handleTicketCreated);
    const cleanupTicketMergeUpdate = signalR.on('TicketMergeUpdate', handleTicketMergeUpdate);
    const cleanupTicketUpdate = signalR.on('TicketUpdate', handleTicketUpdate);
    const cleanupTicketUpdated = signalR.on('TicketUpdated', handleTicketUpdated);
    const cleanupTicketActivity = signalR.on('TicketActivity', handleTicketActivity);
    const cleanupCommentAdded = signalR.on('CommentAdded', handleCommentAdded);

    // Cleanup function - use the cleanup functions returned by on()
    return () => {
      cleanupTicketCreated();
      cleanupTicketMergeUpdate();
      cleanupTicketUpdate();
      cleanupTicketUpdated();
      cleanupTicketActivity();
      cleanupCommentAdded();
    };
  }, [isConnected, signalR, queryClient, actualIsCustomerPortal]);

  // Auto-select all organizations if none selected (for agent portal, only after filter restoration is complete)
  useEffect(() => {
    if (isInitialized && !actualIsCustomerPortal && availableOrganizations.length > 0 && organizationFilters.length === 0) {
      updateFilters({ organizationIds: availableOrganizations.map(org => org.id) });
    }
  }, [isInitialized, actualIsCustomerPortal, availableOrganizations, organizationFilters.length, updateFilters]);

  // Sync search input with URL params when they change
  useEffect(() => {
    if (!actualIsCustomerPortal && searchParams) {
      setSearchInput(searchParams.search || "");
    }
  }, [searchParams?.search, actualIsCustomerPortal]);

  const handleSearch = () => {
    if (actualIsCustomerPortal) {
      setCustomerSearchQuery(searchInput);
      setCustomerCurrentPage(1);
    } else {
      updateFilters({ search: searchInput || undefined, page: 1 });
    }
  };

  const handleSort = (field: string) => {
    if (actualIsCustomerPortal) {
      if (customerSortBy === field) {
        setCustomerSortDescending(!customerSortDescending);
      } else {
        setCustomerSortBy(field);
        setCustomerSortDescending(true);
      }
    } else {
      const newSortDescending = sortBy === field ? !sortDescending : true;
      updateFilters({ sortBy: field, sortDescending: newSortDescending });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (actualIsCustomerPortal) {
      setCustomerPageSize(newPageSize);
      setCustomerCurrentPage(1);
    } else {
      updateFilters({ pageSize: newPageSize, page: 1 });
    }
  };

  // Show skeleton while filters are being restored (helpdesk mode only)
  if (!actualIsCustomerPortal && !isInitialized) {
    return <TicketListSkeleton />;
  }

  // Show full skeleton only when no data exists and loading
  if (isLoading && !data) {
    return <TicketListSkeleton />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Tickets</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Manage tickets</p>
        </div>
        {actualIsCustomerPortal ? (
          <Button
            onClick={() =>
              (window.location.href = "/customer-portal/tickets/new")
            }
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Ticket
          </Button>
        ) : (
          <Link to={getPath.newTicket()} className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Create Ticket
            </Button>
          </Link>
        )}
      </div>

      {/* Quick View Tabs - only show in agent portal */}
      {!actualIsCustomerPortal && user && (
        <div className="mb-4">
          <Tabs
            value={isMyOpenTicketsActive ? "my-open" : showFollowing ? "following" : "all"}
            onValueChange={(value) => {
              if (value === "my-open") {
                updateFilters({
                  assignees: [user.id],
                  statuses: openStatuses,
                  following: undefined,
                  organizationIds: availableOrganizations.map(org => org.id),
                  page: 1
                });
              } else if (value === "following") {
                // Every ticket you follow, in any status, across your organizations
                updateFilters({
                  following: true,
                  assignees: undefined,
                  statuses: undefined,
                  organizationIds: availableOrganizations.map(org => org.id),
                  page: 1
                });
              } else {
                updateFilters({
                  assignees: undefined,
                  statuses: undefined,
                  following: undefined,
                  page: 1
                });
              }
            }}
          >
            <TabsList>
              <TabsTrigger value="all">All Tickets</TabsTrigger>
              <TabsTrigger value="my-open">My Open Tickets</TabsTrigger>
              <TabsTrigger value="following">Following</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Filters */}
      <CollapsibleFilterBar
        storageKey="helpdesk.tickets.filtersCollapsed"
        activeFilterCount={activeFilterCount}
        searchSection={
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Search
            </label>
            <div className="flex">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="rounded-r-none pr-8"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      if (actualIsCustomerPortal) {
                        setCustomerSearchQuery("");
                        setCustomerCurrentPage(1);
                      } else {
                        updateFilters({ search: undefined, page: 1 });
                      }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button
                onClick={handleSearch}
                className="rounded-l-none px-3 sm:px-4"
                disabled={isLoading}
              >
                <span className="hidden sm:inline">Search</span>
                <span className="sm:hidden">Go</span>
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3 sm:space-y-4">
          {/* First row: Primary filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Organization filter - show first since it affects category availability */}
            {!actualIsCustomerPortal && availableOrganizations.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Organization
                </label>
                <MultiSelectFilter
                  label="Organization"
                  options={availableOrganizations.map(org => ({ value: org.id, label: org.name }))}
                  selectedValues={organizationFilters}
                  onChange={(values) => {
                    updateFilters({ organizationIds: values, assignees: undefined, statuses: undefined, page: 1 });
                  }}
                  requireAtLeastOne={true}
                  showSelectAllControls={true}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <MultiSelectFilter
                label="Status"
                options={safeStatuses.map(s => ({ value: s.value.toString(), label: s.label }))}
                selectedValues={statusFilters.map(s => s.toString())}
                onChange={(values) => {
                  if (actualIsCustomerPortal) {
                    // Customer portal uses single select fallback
                    const newValue = values.length > 0 ? parseInt(values[values.length - 1]) as TicketStatus : "";
                    setCustomerStatusFilter(newValue);
                    setCustomerCurrentPage(1);
                  } else {
                    updateFilters({ statuses: values.map(v => parseInt(v)), page: 1 });
                  }
                }}
                showSelectAllControls={true}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Priority
              </label>
              <MultiSelectFilter
                label="Priority"
                options={safePriorities.map(p => ({ value: p.value.toString(), label: p.label }))}
                selectedValues={priorityFilters.map(p => p.toString())}
                onChange={(values) => {
                  if (actualIsCustomerPortal) {
                    // Customer portal uses single select fallback
                    const newValue = values.length > 0 ? parseInt(values[values.length - 1]) as Priority : "";
                    setCustomerPriorityFilter(newValue);
                    setCustomerCurrentPage(1);
                  } else {
                    updateFilters({ priorities: values.map(v => parseInt(v)), page: 1 });
                  }
                }}
                showSelectAllControls={true}
              />
            </div>
          </div>

          {/* Second row: Secondary filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
            {/* Category filter - only show when exactly one organization is selected */}
            {(actualIsCustomerPortal || selectedOrgIds.length === 1) && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Category
                </label>
                <TreeMultiSelectFilter
                  label="Category"
                  options={safeCategories.map(categoryToOption)}
                  selectedIds={categoryFilters}
                  footnote="A category includes its subcategories"
                  onChange={(values) => {
                    if (actualIsCustomerPortal) {
                      // Customer portal uses single select fallback
                      const newValue = values.length > 0 ? values[values.length - 1] : "";
                      setCustomerCategoryFilter(newValue);
                      setCustomerCurrentPage(1);
                    } else {
                      updateFilters({ categories: values.length > 0 ? values : undefined, page: 1 });
                    }
                  }}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Assignee
              </label>
              {actualIsCustomerPortal ? (
                <MultiSelectFilter
                  label="Assignee"
                  options={[
                    { value: "unassigned", label: "Unassigned" },
                    ...safeUsers.map(u => ({ value: u.id, label: u.fullName }))
                  ]}
                  selectedValues={assigneeFilters}
                  onChange={(values) => {
                    // Customer portal uses single select fallback
                    const newValue = values.length > 0 ? values[values.length - 1] : "";
                    setCustomerAssigneeFilter(newValue);
                    setCustomerCurrentPage(1);
                  }}
                  showSelectAllControls={true}
                />
              ) : (
                <AssigneeMultiSelectFilter
                  users={safeUsers}
                  selectedIds={assigneeFilters}
                  loaded={usersLoaded}
                  onChange={(values) => {
                    updateFilters({ assignees: values.length > 0 ? values : undefined, page: 1 });
                  }}
                />
              )}
            </div>

            {/* Customer filter - staff helpdesk only */}
            {!actualIsCustomerPortal && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Customer
                </label>
                <CustomerMultiSelectFilter
                  organizationIds={selectedOrgIds}
                  selectedIds={customerFilters}
                  selectedCustomers={safeSelectedCustomers}
                  showOrganization={availableOrganizations.length > 1}
                  onChange={(values) => {
                    updateFilters({ customers: values.length > 0 ? values : undefined, page: 1 });
                  }}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Created From
              </label>
              <DatePicker
                value={fromDate}
                onChange={(newValue) => {
                  if (actualIsCustomerPortal) {
                    setCustomerFromDate(newValue || "");
                    setCustomerCurrentPage(1);
                  } else {
                    updateFilters({ fromDate: newValue, page: 1 });
                  }
                }}
                placeholder="Select date"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Created To
              </label>
              <DatePicker
                value={toDate}
                onChange={(newValue) => {
                  if (actualIsCustomerPortal) {
                    setCustomerToDate(newValue || "");
                    setCustomerCurrentPage(1);
                  } else {
                    updateFilters({ toDate: newValue, page: 1 });
                  }
                }}
                placeholder="Select date"
              />
            </div>

            {/* Overdue filter - only show in agent portal */}
            {!actualIsCustomerPortal && (
              <div className="flex items-end">
                <label
                  className={`flex items-center gap-2 w-full h-[42px] px-3 rounded-md border cursor-pointer transition-colors ${
                    showOverdue
                      ? "bg-red-50 border-red-300 dark:bg-red-950 dark:border-red-800"
                      : "border-input hover:bg-muted/50"
                  }`}
                >
                  <Checkbox
                    checked={showOverdue}
                    onCheckedChange={(checked) => {
                      updateFilters({ showOverdue: checked === true ? true : undefined, page: 1 });
                    }}
                    className="data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                  />
                  <AlertTriangle className={`h-4 w-4 ${showOverdue ? "text-red-500" : "text-muted-foreground"}`} />
                  <span className={`text-sm font-medium ${showOverdue ? "text-red-700 dark:text-red-300" : "text-foreground"}`}>
                    Overdue
                  </span>
                </label>
              </div>
            )}

            {/* Following filter - tickets you follow (agent portal only) */}
            {!actualIsCustomerPortal && (
              <div className="flex items-end">
                <label
                  className={`flex items-center gap-2 w-full h-[42px] px-3 rounded-md border cursor-pointer transition-colors ${
                    showFollowing
                      ? "bg-primary/10 border-primary/40"
                      : "border-input hover:bg-muted/50"
                  }`}
                >
                  <Checkbox
                    checked={showFollowing}
                    onCheckedChange={(checked) => {
                      updateFilters({ following: checked === true ? true : undefined, page: 1 });
                    }}
                  />
                  <Eye className={`h-4 w-4 ${showFollowing ? "text-primary" : "text-muted-foreground"}`} />
                  <span className="text-sm font-medium text-foreground">
                    Following
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  if (actualIsCustomerPortal) {
                    setSearchInput("");
                    setCustomerSearchQuery("");
                    setCustomerStatusFilter("");
                    setCustomerPriorityFilter("");
                    setCustomerAssigneeFilter("");
                    setCustomerCategoryFilter("");
                    setCustomerFromDate("");
                    setCustomerToDate("");
                    setCustomerCurrentPage(1);
                  } else {
                    setSearchInput("");
                    clearPersistedFilters();
                  }
                }}
                className="w-full h-[42px]"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </CollapsibleFilterBar>

      {/* Error Display */}
      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error?.message || "An error occurred while loading tickets"}
        </div>
      )}

      {/* Tickets Table */}
      <Card>
        <div className="relative">
          {/* Bounding the height is what makes horizontal scrolling usable: `overflow-auto`
              scrolls on both axes, so an unbounded container puts its horizontal scrollbar at
              the bottom of all 25 rows, far below the fold. It also gives the sticky header a
              scrollport to stick to — sticky resolves against the nearest scrolling ancestor. */}
          <div
            ref={tableScrollRef}
            onScroll={updateScrollEdges}
            {...tableDragProps}
            className="max-h-[calc(100vh-22rem)] overflow-auto"
          >
            <table className={`w-full table-fixed ${tableMinWidthClass}`}>
              <thead className="sticky top-0 z-20 bg-muted">
                <tr>
                  <th
                    className={`${TH_CLASS} sticky left-0 z-30 w-[90px] bg-muted`}
                    onClick={() => handleSort("ticketnumber")}
                  >
                    Ticket #
                    {sortBy === "ticketnumber" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  {/* No width — Subject absorbs whatever space the fixed columns leave over. */}
                  <th
                    className={TH_CLASS}
                    onClick={() => handleSort("subject")}
                  >
                    Subject
                    {sortBy === "subject" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  {!actualIsCustomerPortal && (
                    <th
                      className={`${TH_CLASS} w-[140px] hidden lg:table-cell`}
                      onClick={() => handleSort("organization")}
                    >
                      Organization
                      {sortBy === "organization" && (
                        <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                      )}
                    </th>
                  )}
                  {!actualIsCustomerPortal && (
                    <th
                      className={`${TH_CLASS} w-[190px] hidden md:table-cell`}
                      onClick={() => handleSort("customer")}
                    >
                      Customer
                      {sortBy === "customer" && (
                        <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                      )}
                    </th>
                  )}
                  <th
                    className={`${TH_CLASS} w-[110px]`}
                    onClick={() => handleSort("status")}
                  >
                    Status
                    {sortBy === "status" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[92px]`}
                    onClick={() => handleSort("priority")}
                  >
                    Priority
                    {sortBy === "priority" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  {!actualIsCustomerPortal && (
                    <th
                      className={`${TH_CLASS} w-[160px]`}
                      onClick={() => handleSort("assignedto")}
                    >
                      Assigned To
                      {sortBy === "assignedto" && (
                        <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                      )}
                    </th>
                  )}
                  <th
                    className={`${TH_CLASS} w-[104px] hidden lg:table-cell`}
                    onClick={() => handleSort("createdat")}
                  >
                    Created
                    {sortBy === "createdat" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[150px] hidden lg:table-cell`}
                    onClick={() => handleSort("dueat")}
                  >
                    Due Date
                    {sortBy === "dueat" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                  <th
                    className={`${TH_CLASS} w-[104px] hidden lg:table-cell`}
                    onClick={() => handleSort("updatedat")}
                  >
                    Updated
                    {sortBy === "updatedat" && (
                      <span className="ml-1">{sortDescending ? "↓" : "↑"}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border">
                {isFetching && tickets.length === 0
                  ? // Loading state - show skeleton rows
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr
                        key={`loading-${index}`}
                        className="animate-pulse bg-background"
                      >
                        <td className={`${TD_CLASS} sticky left-0 z-10 bg-inherit`}>
                          <div className="h-4 bg-muted rounded w-16"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-4 bg-muted rounded w-32"></div>
                        </td>
                        {!actualIsCustomerPortal && (
                          <td className={`${TD_CLASS} hidden lg:table-cell`}>
                            <div className="h-4 bg-muted rounded w-20"></div>
                          </td>
                        )}
                        {!actualIsCustomerPortal && (
                          <td className={`${TD_CLASS} hidden md:table-cell`}>
                            <div className="h-4 bg-muted rounded w-24"></div>
                          </td>
                        )}
                        <td className={TD_CLASS}>
                          <div className="h-5 bg-muted rounded-full w-16"></div>
                        </td>
                        <td className={TD_CLASS}>
                          <div className="h-5 bg-muted rounded-full w-12"></div>
                        </td>
                        {!actualIsCustomerPortal && (
                          <td className={TD_CLASS}>
                            <div className="h-4 bg-muted rounded w-20"></div>
                          </td>
                        )}
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                        <td className={`${TD_CLASS} hidden lg:table-cell`}>
                          <div className="h-4 bg-muted rounded w-20"></div>
                        </td>
                      </tr>
                    ))
                  : tickets.map((ticket) => (
                      // Row backgrounds must be opaque: the sticky Ticket # cell below inherits
                      // this colour, and a translucent one would paint over the row's own hover
                      // tint and render that single cell noticeably darker than its neighbours.
                      <tr
                        key={ticket.id}
                        className="bg-background hover:bg-muted cursor-pointer"
                        onClick={() => {
                          if (actualIsCustomerPortal) {
                            // For customer portal, use window.location since it's outside the app router
                            window.location.href = `/customer-portal/tickets/${ticket.id}`;
                          } else {
                            // Use TanStack Router navigation for internal routes
                            navigateTo.ticket(ticket.id);
                          }
                        }}
                      >
                        <td className={`${TD_CLASS} sticky left-0 z-10 bg-inherit`}>
                          <span className="text-blue-600 font-medium text-xs sm:text-sm">
                            #{ticket.ticketNumber}
                          </span>
                        </td>
                        {/* Subject is the one column allowed to wrap. TD_CLASS carries `truncate`
                            (white-space: nowrap), which would defeat line clamping, so this cell
                            overrides it with whitespace-normal. Clamped to two lines rather than
                            left unbounded: a 90-character subject would otherwise make one row
                            four times the height of its neighbours and break the table's rhythm. */}
                        <td className={`${TD_CLASS} whitespace-normal align-middle`}>
                          <div
                            className="text-foreground font-medium text-xs sm:text-sm flex items-start gap-1.5 min-w-0"
                            title={
                              ticket.mergedIntoTicketId
                                ? `${ticket.subject} (merged${ticket.mergedIntoTicketNumber ? ` into #${ticket.mergedIntoTicketNumber}` : ""})`
                                : ticket.subject
                            }
                          >
                            {ticket.mergedIntoTicketId && (
                              <GitMerge className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-indigo-600 dark:text-indigo-400" />
                            )}
                            <span className="line-clamp-2 break-words">{ticket.subject}</span>
                          </div>
                        </td>
                        {!actualIsCustomerPortal && (
                          <td className={`${TD_CLASS} hidden lg:table-cell`}>
                            <div
                              className="truncate text-xs sm:text-sm text-foreground"
                              title={ticket.organizationName}
                            >
                              {ticket.organizationName}
                            </div>
                          </td>
                        )}
                        {!actualIsCustomerPortal && (
                          <td className={`${TD_CLASS} hidden md:table-cell`}>
                            <div
                              className="text-xs sm:text-sm font-medium text-foreground truncate"
                              title={ticket.customer.fullName}
                            >
                              {ticket.customer.fullName}
                            </div>
                            <div
                              className="text-xs text-muted-foreground truncate"
                              title={ticket.customer.email}
                            >
                              {ticket.customer.email}
                            </div>
                          </td>
                        )}
                        <td className={TD_CLASS}>
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${TicketStatusColors[ticket.status]}`}
                          >
                            {TicketStatusLabels[ticket.status]}
                          </span>
                        </td>
                        <td className={TD_CLASS}>
                          <span
                            className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${PriorityColors[ticket.priority]}`}
                          >
                            {PriorityLabels[ticket.priority]}
                          </span>
                        </td>
                        {!actualIsCustomerPortal && (
                          // Deliberately not truncated — this cell owns a dropdown trigger, and
                          // clipping it buys nothing now that the column has a reserved width.
                          // Also deliberately never hidden: it is the only way to assign a ticket
                          // without opening it, and a breakpoint that drops it takes the action
                          // away entirely rather than pushing it off-screen where a scroll or
                          // drag can still reach it.
                          <td
                            className="px-2 sm:px-3 py-2 sm:py-3 whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AssigneeSelector
                              ticketId={ticket.id}
                              ticketOrganizationId={ticket.organizationId}
                              currentAssignee={ticket.assignedTo || null}
                              ticket={ticket}
                              disabled={!!ticket.mergedIntoTicketId}
                            />
                          </td>
                        )}
                        {/* Dates render date-only to keep three date columns affordable; the
                            full timestamp stays available on hover. */}
                        <td
                          className={`${TD_CLASS} hidden lg:table-cell text-xs text-muted-foreground`}
                          title={formatDateTime(ticket.createdAt)}
                        >
                          {formatDateOnly(ticket.createdAt)}
                        </td>
                        <td
                          className={`${TD_CLASS} hidden lg:table-cell text-xs`}
                          title={
                            ticket.dueAt ? formatDateTime(ticket.dueAt) : undefined
                          }
                        >
                          {ticket.dueAt ? (
                            <div className="flex items-center gap-1">
                              <span
                                className={
                                  ticket.isOverdue
                                    ? "text-red-600 font-medium"
                                    : "text-muted-foreground"
                                }
                              >
                                {formatDateOnly(ticket.dueAt)}
                              </span>
                              {ticket.isOverdue && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                  Overdue
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td
                          className={`${TD_CLASS} hidden lg:table-cell text-xs text-muted-foreground`}
                          title={formatDateTime(ticket.updatedAt)}
                        >
                          {formatDateOnly(ticket.updatedAt)}
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {/* Sit outside the scrollport so they stay pinned to the card edges rather than
              travelling with the content. `foreground` inverts with the theme, so this reads
              as a shadow on light and a soft haze on dark. The left one starts at 88px — the
              frozen Ticket # column casts the shadow, so it must not sit under it. */}
          {scrollEdges.left && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-[88px] w-6 bg-gradient-to-r from-foreground/20 to-transparent"
            />
          )}
          {scrollEdges.right && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-foreground/20 to-transparent"
            />
          )}
        </div>

        {/* Pagination and Record Count */}
        <div className="bg-background px-3 sm:px-6 py-2 sm:py-3 border-t border-border">
          {totalPages > 1 ? (
            <div className="flex items-center justify-between">
              {/* Mobile pagination - stacked layout */}
              <div className="flex flex-col gap-3 sm:hidden w-full">
                {/* Page size and record count */}
                <div className="flex flex-col gap-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <label className="text-xs text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs text-foreground">per page</span>
                  </div>
                  <p className="text-xs text-foreground">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{" "}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>

                {/* Pagination buttons */}
                <div className="flex justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newPage = Math.max(1, currentPage - 1);
                      if (actualIsCustomerPortal) {
                        setCustomerCurrentPage(newPage);
                      } else {
                        updateFilters({ page: newPage });
                      }
                    }}
                    disabled={currentPage === 1 || isLoading}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const newPage = Math.min(totalPages, currentPage + 1);
                      if (actualIsCustomerPortal) {
                        setCustomerCurrentPage(newPage);
                      } else {
                        updateFilters({ page: newPage });
                      }
                    }}
                    disabled={currentPage === totalPages || isLoading}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-foreground">per page</span>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{" "}
                      of <span className="font-medium">{totalCount}</span> results
                    </p>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        if (actualIsCustomerPortal) {
                          setCustomerCurrentPage(newPage);
                        } else {
                          updateFilters({ page: newPage });
                        }
                      }}
                      disabled={currentPage === 1 || isLoading}
                      className="rounded-l-md"
                    >
                      Previous
                    </Button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNumber =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) +
                        i;
                      return (
                        <Button
                          key={pageNumber}
                          variant={
                            pageNumber === currentPage ? "default" : "outline"
                          }
                          onClick={() => {
                            if (actualIsCustomerPortal) {
                              setCustomerCurrentPage(pageNumber);
                            } else {
                              updateFilters({ page: pageNumber });
                            }
                          }}
                          disabled={isLoading}
                          className="rounded-none"
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}

                    <Button
                      variant="outline"
                      onClick={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        if (actualIsCustomerPortal) {
                          setCustomerCurrentPage(newPage);
                        } else {
                          updateFilters({ page: newPage });
                        }
                      }}
                      disabled={currentPage === totalPages || isLoading}
                      className="rounded-r-md"
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          ) : (
            // Show record count and page size selector even when there's only one page
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <label className="text-xs sm:text-sm text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-xs sm:text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs sm:text-sm text-foreground">per page</span>
                </div>
                <p className="text-xs sm:text-sm text-foreground text-center sm:text-left">
                  Showing{" "}
                  <span className="font-medium">
                    {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {tickets.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No tickets found
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery ||
              statusFilters.length > 0 ||
              priorityFilters.length > 0 ||
              assigneeFilters.length > 0 ||
              categoryFilters.length > 0 ||
              organizationFilters.length > 0 ||
              fromDate ||
              toDate
                ? "Try adjusting your filters to see more results."
                : "Get started by creating your first ticket."}
            </p>
            {actualIsCustomerPortal ? (
              <Button
                onClick={() =>
                  (window.location.href = "/customer-portal/tickets/new")
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Ticket
              </Button>
            ) : (
              <Link to={getPath.newTicket()}>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Ticket
                </Button>
              </Link>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
